import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getTextField } from '@lender/utils';
import { FormHelperText } from '@mui/material';
import ModalFormWrapper from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import { documentBundleApi } from 'app/services/api';
import {
  FormAutocompleteOption,
  onAutocompleteChangeInForm,
} from 'app/shared/components/form/Autocomplete';
import { AutocompleteCDS, CheckboxCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';
import { boolean, object } from 'yup';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';
import { getFileNameFromContentDispositionHeader } from 'utils/file';
import { logger } from 'utils/logger';
import { downloadOrShareBlob } from 'utils/nativeFile';

import { closeDialogAction, showErrorMessageAction } from 'store/global/actions';

import { LoanApplicationDocumentsTestIds } from '../../LoanApplicationDocuments.testIds';

const schema = object().shape({
  bundleType: getTextField(),
  ignoreMissingDocuments: boolean(),
});

const GenerateBundleFormTab = ({
  documentBundleTypes,
  error,
  hasMissingDocuments,
  hasNoDocuments,
  formRef = null,
  isLoading,
  loanParams,
  setGenerateBundleState,
  setChosenMissingDocumentTypeId,
  initialGenerateBundleState,
  isUseEffect,
  bundleType,
  ignoreMissingDocuments,
}) => {
  const dispatch = useDispatch();
  const [separateFiles, setSeparateFiles] = useState(false);

  const {
    form,
    handleChange: onChange,
    validationErrors,
    setSchema,
    isFormChanged,
    setInForm,
    validateField,
  } = useForm({
    bundleType,
    ignoreMissingDocuments,
  });

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  useEffectSafeStrictMode(() => {
    const validateBundleGeneration = async () => {
      try {
        setInForm('ignoreMissingDocuments', false, false);
        setGenerateBundleState({
          ...initialGenerateBundleState,
          isLoading: true,
          bundleType: form.bundleType?.documentBundleTypeId,
        });

        const {
          data: { isAnyDocumentExists, missingMandatoryDocumentTypeIds },
        } = await documentBundleApi.validateBundleGeneration({
          ...loanParams,
          documentBundleTypeId: form.bundleType?.documentBundleTypeId,
        });

        if (missingMandatoryDocumentTypeIds.length) {
          setChosenMissingDocumentTypeId(missingMandatoryDocumentTypeIds[0]);
          setGenerateBundleState(state => ({
            ...state,
            hasMissingDocuments: true,
            missingDocuments: missingMandatoryDocumentTypeIds.map(docTypeId => ({
              docTypeId,
              isReady: false,
              uploadedFiles: [],
            })),
            error: 'You have missing documents',
          }));
        }

        if (!missingMandatoryDocumentTypeIds.length && !isAnyDocumentExists) {
          setGenerateBundleState(state => ({
            ...state,
            hasNoDocuments: true,
            error: "You have no documents, so you can't generate bundle",
          }));
        }
      } catch (err) {
        logger.error(err);
        dispatch(showErrorMessageAction({ message: "Couldn't validate bundle generation", err }));
      } finally {
        setGenerateBundleState(state => ({ ...state, isLoading: false }));
      }
    };

    if (form.bundleType?.documentBundleTypeId && isUseEffect.current) {
      validateBundleGeneration();
    }
  }, [
    form.bundleType?.documentBundleTypeId,
    loanParams,
    dispatch,
    setInForm,
    setGenerateBundleState,
    initialGenerateBundleState,
    setChosenMissingDocumentTypeId,
    isUseEffect,
  ]);

  useEffect(() => {
    if (!isUseEffect.current) {
      isUseEffect.current = true;
    }

    return () => {
      isUseEffect.current = false;
    };
  }, [isUseEffect]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      setGenerateBundleState(state => ({ ...state, isLoading: true }));

      documentBundleApi
        .getBundle({
          ...loanParams,
          documentBundleTypeId: form.bundleType?.documentBundleTypeId,
          separateFiles,
        })
        .then(res => {
          const extension = separateFiles ? 'zip' : 'pdf';
          const fileName = getFileNameFromContentDispositionHeader({
            contentDisposition: res.headers['content-disposition'],
            extension,
          });
          downloadOrShareBlob(res.data, `${fileName ?? 'bundle'}.${extension}`);
          dispatch(closeDialogAction());
        })
        .catch(err => {
          logger.error(err);
          setGenerateBundleState(state => ({ ...state, isLoading: false }));
          dispatch(showErrorMessageAction({ message: "Couldn't generate bundle", err }));
        });
    },
    [
      dispatch,
      form.bundleType?.documentBundleTypeId,
      loanParams,
      separateFiles,
      setGenerateBundleState,
    ],
  );

  const onChangeSeparateFiles = useCallback(e => {
    setSeparateFiles(e.target.checked);
  }, []);

  const onChangeIgnoreCheckbox = useCallback(
    (e, validationContext) => {
      onChange(e, validationContext);
      setGenerateBundleState(state => ({
        ...state,
        ignoreMissingDocuments: !ignoreMissingDocuments,
      }));
    },
    [setGenerateBundleState, ignoreMissingDocuments, onChange],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  return (
    <ModalFormWrapper
      formRef={formRef}
      formTestId="generate-bundle-form"
      isFormChanged={isFormChanged}
      isSubmitButtonDisabled={
        (hasMissingDocuments && !form.ignoreMissingDocuments) || hasNoDocuments
      }
      isSubmitting={isLoading}
      submitButtonName="Generate & Download"
      submitButtonTestId="generate-bundle-button"
      onSubmit={onSubmit}
    >
      <AutocompleteCDS
        disableClearable
        fullWidth
        required
        OptionContent={FormAutocompleteOption}
        containerClassName="mb-24"
        dataTestId={LoanApplicationDocumentsTestIds.ASSOCIATIONS_FORM_BUNDLES_DROPDOWN_SELECT}
        dataTestIdOption={LoanApplicationDocumentsTestIds.ASSOCIATIONS_FORM_BUNDLES_OPTION}
        error={!!validationErrors.bundleType}
        helperText={validationErrors.bundleType}
        label="Bundle type"
        name="bundleType"
        optionName="documentBundleTypeName"
        options={documentBundleTypes}
        value={form.bundleType || ''}
        variant="outlined"
        onChange={onAutocompleteChange}
      />
      <CheckboxCDS
        containerClassName="mb-24"
        dataTestId={LoanApplicationDocumentsTestIds.ASSOCIATIONS_FORM_SEPARATE_FILES_CHECKBOX}
        label="Separate into individual files"
        name="separateFiles"
        value={separateFiles}
        onChange={onChangeSeparateFiles}
      />
      <div className="flex flex-col justify-between items-center">
        <div className="flex flex-col justify-between items-center">
          <FormHelperText className="mb-20">{error}</FormHelperText>
          {hasMissingDocuments && (
            <CheckboxCDS
              containerClassName="mb-24"
              data-testid={
                LoanApplicationDocumentsTestIds.ASSOCIATIONS_FORM_IGNORE_MISSING_DOCUMENTS_CHECKBOX
              }
              label="Ignore missing documents"
              name="ignoreMissingDocuments"
              value={form.ignoreMissingDocuments}
              onChange={onChangeIgnoreCheckbox}
            />
          )}
        </div>
      </div>
    </ModalFormWrapper>
  );
};

GenerateBundleFormTab.propTypes = {
  loanParams: PropTypes.object.isRequired,
  error: PropTypes.string.isRequired,
  bundleType: PropTypes.string.isRequired,
  initialGenerateBundleState: PropTypes.object.isRequired,
  isUseEffect: PropTypes.object.isRequired,
  hasMissingDocuments: PropTypes.bool.isRequired,
  ignoreMissingDocuments: PropTypes.bool.isRequired,
  hasNoDocuments: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  documentBundleTypes: PropTypes.array.isRequired,
  setChosenMissingDocumentTypeId: PropTypes.func.isRequired,
  setGenerateBundleState: PropTypes.func.isRequired,
  formRef: PropTypes.shape({ current: PropTypes.object }),
};

export default GenerateBundleFormTab;
